import { useEffect, useState } from "react";
import { Box, Chip } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { useSnackbar } from "notistack";
import { SectionCard, PersonAvatar, EmptyState } from "../../../mui";
import { brainTokens } from "../../../design/brainTheme";
import { listPeopleApi, getPresence, presignFile, type PersonGridRow, type PresenceSession } from "../../../lib/brain-api";

const c = brainTokens.color;

export function WhoAreWeTab() {
  const { enqueueSnackbar } = useSnackbar();
  const [people, setPeople] = useState<PersonGridRow[]>([]);
  const [sessions, setSessions] = useState<PresenceSession[]>([]);
  const [orgUrl, setOrgUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [p, presence] = await Promise.all([listPeopleApi(), getPresence()]);
        if (cancelled) return;
        setPeople(p);
        setSessions(presence.sessions);
        try {
          const url = await presignFile("customers/aeronet/org-chart.html");
          if (!cancelled) setOrgUrl(url);
        } catch {
          if (!cancelled) setOrgUrl(null);
        }
      } catch (e) {
        enqueueSnackbar(e instanceof Error ? e.message : "Failed to load people", { variant: "error" });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enqueueSnackbar]);

  const presenceFor = (slug: string) => {
    const s = sessions.find(
      (x) => x.slug === slug && (x.state === "active" || x.state === "held-unpushed")
    );
    return s?.state ?? null;
  };

  if (loading) return <Box sx={{ fontSize: 13, color: c.muted2 }}>Loading…</Box>;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <SectionCard label="Org chart">
        {orgUrl ? (
          <Box
            component="iframe"
            src={orgUrl}
            title="Aeronet org chart"
            sandbox="allow-top-navigation-by-user-activation"
            sx={{ width: "100%", height: 360, border: `1px solid ${c.border}`, borderRadius: 1 }}
          />
        ) : (
          <EmptyState icon="users" title="Org chart coming soon">
            Admin can upload <Box component="span" sx={{ fontFamily: brainTokens.font.mono, fontSize: 12 }}>customers/aeronet/org-chart.html</Box> to the store. A draft lives in the design docs.
          </EmptyState>
        )}
      </SectionCard>

      <Box>
        <Box sx={{ fontSize: 16, fontWeight: 600, color: c.ink, mb: 1.5 }}>People</Box>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
            gap: 1.5,
          }}
        >
          {people.map((p) => (
            <Box
              key={p.slug}
              component={RouterLink}
              to={`/people/${encodeURIComponent(p.slug)}`}
              sx={{
                textDecoration: "none",
                border: `1px solid ${c.border}`,
                borderRadius: `${brainTokens.radius.panel}px`,
                p: 2,
                background: c.pageBg,
                "&:hover": { borderColor: c.hoverBorder },
                minWidth: 0,
              }}
            >
              <Box sx={{ display: "flex", gap: 1.25, alignItems: "center", mb: 1 }}>
                <PersonAvatar
                  name={p.displayName}
                  avatarUrl={p.avatarUrl}
                  size={44}
                  presence={presenceFor(p.slug)}
                />
                <Box sx={{ minWidth: 0 }}>
                  <Box sx={{ fontWeight: 600, color: c.ink, fontSize: 14.5 }}>{p.displayName}</Box>
                  <Box sx={{ fontSize: 12, color: c.muted2 }}>{p.role}</Box>
                </Box>
              </Box>
              <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mb: 1 }}>
                {p.departments.map((d) => (
                  <Chip key={d} size="small" label={d} />
                ))}
              </Box>
              <Box sx={{ fontSize: 13, color: c.ink3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {p.blurb || "—"}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      <SectionCard label="Top brain feeders this week">
        <Box sx={{ fontSize: 13, color: c.muted2 }}>
          Capture counts land here once the weekly digest job pairs people with their AI pets —
          playful, never a scoreboard.
        </Box>
      </SectionCard>
    </Box>
  );
}
